import React, { useState, useEffect } from 'react';
import swal from 'sweetalert';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Countdown from 'react-countdown';
import { FaTicketAlt, FaTrophy, FaCoins, FaClock, FaUsers, FaArrowRight, FaWallet } from 'react-icons/fa';
import { processStakePayment } from '../services/paymentService';
import { Helmet } from 'react-helmet-async';

// TV Screen Effect
const flicker = keyframes`
  0% { opacity: 0.8; }
  10% { opacity: 0.7; }
  20% { opacity: 0.85; }
  30% { opacity: 0.75; }
  40% { opacity: 0.9; }
  50% { opacity: 0.8; }
  60% { opacity: 0.95; }
  70% { opacity: 0.85; }
  80% { opacity: 0.98; }
  90% { opacity: 0.9; }
  100% { opacity: 1; }
`;

const TVScreen = styled.div`
  background-color: #000;
  border: 20px solid #333;
  border-radius: 20px;
  padding: 2rem;
  margin: 2rem 0;
  position: relative;
  overflow: hidden;
  box-shadow: 0 0 50px rgba(0, 0, 0, 0.7);
  animation: ${flicker} 5s infinite alternate;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      to bottom,
      rgba(18, 16, 16, 0) 50%,
      rgba(0, 0, 0, 0.25) 50%
    );
    background-size: 100% 4px;
    z-index: 1;
    pointer-events: none;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      to right,
      rgba(18, 16, 16, 0) 50%,
      rgba(0, 0, 0, 0.1) 50%
    );
    background-size: 4px 100%;
    z-index: 1;
    pointer-events: none;
  }
`;

const TVContent = styled.div`
  position: relative;
  z-index: 2;
  background: rgba(0, 0, 0, 0.7);
  border: 2px solid #333;
  border-radius: 10px;
  padding: 2rem;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Title = styled.h1`
  font-family: 'Press Start 2P', cursive;
  color: ${({ theme }) => theme.colors.accent};
  text-align: center;
  margin-bottom: 2rem;
  text-shadow: 0 0 10px rgba(255, 230, 109, 0.5);
  font-size: 2.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.8rem;
  }
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
  margin-bottom: 2rem;
  font-size: 1.2rem;
  max-width: 800px;
`;

const CountdownContainer = styled.div`
  background: rgba(0, 0, 0, 0.7);
  padding: 1.5rem 2rem;
  border-radius: 10px;
  margin: 1.5rem 0;
  text-align: center;
  border: 2px solid ${({ theme }) => theme.colors.primary};
`;

const CountdownTitle = styled.h3`
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const CountdownTimer = styled.div`
  font-family: 'VT323', monospace;
  font-size: 3rem;
  color: ${({ theme }) => theme.colors.accent};
  text-shadow: 0 0 10px rgba(255, 230, 109, 0.5);
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 2rem;
  }
`;

const StatsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin: 2rem 0;
  flex-wrap: wrap;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  padding: 1.5rem;
  border-radius: 10px;
  text-align: center;
  min-width: 180px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.accent};
  margin: 0.5rem 0;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const PlayButton = styled.button`
  background: linear-gradient(45deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  color: #fff;
  border: none;
  padding: 1rem 2.5rem;
  border-radius: 50px;
  font-family: 'Press Start 2P', cursive;
  font-size: 1.2rem;
  cursor: pointer;
  margin: 1.5rem 0;
  text-transform: uppercase;
  letter-spacing: 2px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  z-index: 1;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(45deg, ${({ theme }) => theme.colors.secondary}, ${({ theme }) => theme.colors.primary});
    z-index: -1;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);
    
    &::before {
      opacity: 1;
    }
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    background: #666;
    cursor: not-allowed;
    transform: none;
    
    &::before {
      display: none;
    }
  }
`;

const InputWithIcon = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  
  svg {
    position: absolute;
    left: 12px;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const WalletInput = styled.input`
  width: 100%;
  padding: 12px 12px 12px 40px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.card};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.9rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary}20;
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const ConnectWalletMessage = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 1rem;
  font-size: 0.9rem;
  text-align: center;
`;

const StakeInputContainer = styled.div`
  display: flex;
  align-items: center;
  margin: 1.5rem 0;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const StakeInput = styled.input`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.text};
  font-size: 1.2rem;
  padding: 1rem 1.5rem;
  width: 120px;
  text-align: right;
  outline: none;
  
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
  &[type=number] {
    -moz-appearance: textfield;
  }
`;

const CurrencyLabel = styled.span`
  background: rgba(0, 0, 0, 0.3);
  color: ${({ theme }) => theme.colors.text};
  padding: 1rem 1.5rem;
  font-weight: bold;
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  margin-top: 1rem;
  text-align: center;
  font-size: 0.9rem;
`;

const HomePage = () => {
  const navigate = useNavigate();
  const [walletAddress, setWalletAddress] = useState('');
  const [gameData, setGameData] = useState({
    nextDraw: null,
    totalPlayers: 0,
    totalPrizes: 0,
    gameId: null,
    isActive: false,
    endTime: null,
    startTime: null,
    status: 'loading' // 'loading', 'active', 'upcoming', 'error'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [stakeAmount, setStakeAmount] = useState(1);
  const [error, setError] = useState('');
  const baseUrl = process.env.REACT_APP_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : '');

  // Fetch active game or next game
  const fetchGameData = async () => {
    try {
      console.log('Fetching game data...');
      // First try to get active game
      const activeResponse = await fetch(`${process.env.REACT_APP_API_URL}/games/active`);
      console.log(activeResponse)
      if (activeResponse.ok) {
        const activeGame = await activeResponse.json();
        console.log(activeGame)
        if (activeGame && activeGame.gameId) {
          setGameData({
            nextDraw: new Date(activeGame.endTime).getTime(),
            totalPlayers: activeGame.players.length || 0,
            totalPrizes: activeGame.players.length * 10 || 0,
            gameId: activeGame.gameId,
            isActive: true,
            endTime: activeGame.endTime,
            startTime: activeGame.startTime,
            status: 'active'
          });
          const genID = localStorage.getItem('genID');
          console.log(genID)
          const players = activeGame.players;
          const genIDisPresent = players.some(player => player.genID === genID);
          if (genIDisPresent) {
            swal('Thank you for staking in this game', 'success');
            localStorage.removeItem('genID');
            setIsLoading(false);
            setError('');
          }
          return;
        }
      }

      // If no active game, get next game
      const nextResponse = await fetch(`${process.env.REACT_APP_API_URL}/games/next`);
      if (nextResponse.ok) {
        const nextGame = await nextResponse.json();
        if (nextGame && nextGame.startTime) {
          setGameData({
            nextDraw: new Date(nextGame.startTime).getTime(),
            totalPlayers: 0, // Reset players for upcoming game
            totalPrizes: 0,  // Reset prize pool for upcoming game
            gameId: nextGame.id,
            isActive: false,
            endTime: nextGame.endTime,
            startTime: nextGame.startTime,
            status: 'upcoming'
          });
        } else {
          // No upcoming game found
          setGameData(prev => ({
            ...prev,
            status: 'no-game',
            nextDraw: null
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching game data:', error);
      setGameData(prev => ({
        ...prev,
        status: 'error'
      }));
      setError('Failed to load game data. Please try again later.');
    }
  };

  useEffect(() => {
    fetchGameData();

    // Set up polling every 30 seconds
    const interval = setInterval(fetchGameData, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleStakeChange = (e) => {
    const value = parseFloat(e.target.value);
    if (isNaN(value) || value < 1) {
      setStakeAmount(1);
    } else {
      setStakeAmount(value);
    }
  };

  const validateEthereumAddress = (address) => {
    // Basic Ethereum address validation
    return /^0x[a-fA-F0-9]{40}$/.test(address);
  };

  const handlePlayClick = async () => {
    if (!walletAddress) {
      setError('Please enter your wallet address');
      return;
    }

    if (!validateEthereumAddress(walletAddress)) {
      setError('Please enter a valid Ethereum wallet address');
      return;
    }

    if (!gameData.gameId) {
      setError('No active game available. Please try again later.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const genID = Math.floor(Math.random() * 1000000)+"."+Date.now();
      // Process payment with OxaPay using the provided wallet address
      const payment = await processStakePayment(stakeAmount, walletAddress, gameData.gameId, genID);

      // Redirect to OxaPay payment page
      // window.location.href = payment.paymentUrl;
      window.open(payment.paymentUrl, '_blank');
      // console.log(payment)

      localStorage.setItem('genID', genID);

    } catch (err) {
      console.error('Payment error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to process payment. Please try again.');
      setIsLoading(false);
    }
  };

  // Countdown renderer with days support
  const renderCountdown = ({ days, hours, minutes, seconds, completed }) => {
    if (completed) {
      return <span>Starting soon...</span>;
    }

    return (
      <span>
        {days > 0 && `${days}d `}
        {String(hours).padStart(2, '0')}:
        {String(minutes).padStart(2, '0')}:
        {String(seconds).padStart(2, '0')}
      </span>
    );
  };

  return (
    <div style={{ paddingTop: '80px' }}>
      <Helmet>
        <title>YooWin — Trust‑Minimized Raffle</title>
        <meta name="description" content="Stake, play, and win with transparent on-chain draws." />
        <link rel="canonical" href={`${baseUrl}/`} />
        <meta property="og:title" content="YooWin — Trust‑Minimized Raffle" />
        <meta property="og:description" content="Stake, play, and win with transparent on-chain draws." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${baseUrl}/`} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'YooWin',
          url: `${baseUrl}/`,
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'YooWin',
          url: `${baseUrl}/`,
          logo: `${baseUrl}/logo192.png`
        })}</script>
      </Helmet>
      <TVScreen>
        <TVContent>
          <Title>Trust-Minimized Raffle</Title>
          <Subtitle>
            Join the next draw and stand a chance to win amazing prizes!<br />
            Win big with every draw!

          </Subtitle>
          <CountdownContainer>
            {gameData.status === 'loading' ? (
              <div>Loading game data...</div>
            ) : gameData.status === 'error' ? (
              <div>Error loading game data. Please refresh the page.</div>
            ) : gameData.status === 'no-game' ? (
              <div>No upcoming games scheduled. Check back later!</div>
            ) : (
              <>
                <CountdownTitle>
                  <FaClock /> {gameData.isActive ? 'Time Remaining:' : 'Next Draw In:'}
                </CountdownTitle>
                <CountdownTimer>
                  <Countdown
                    date={gameData.nextDraw}
                    renderer={renderCountdown}
                    onComplete={() => {
                      // Refresh game data when countdown completes
                      fetchGameData();
                    }}
                  />
                </CountdownTimer>
                {gameData.isActive ? (
                  <div style={{ marginTop: '10px', color: '#4caf50', fontSize: '0.9rem' }}>
                    Game in progress! Join now for a chance to win!
                  </div>
                ) : (
                  <div style={{ marginTop: '10px', color: '#ffd700', fontSize: '0.9rem' }}>
                    Next game starts soon! Get ready to play!
                  </div>
                )}
              </>
            )}
          </CountdownContainer>
          {gameData.isActive ? (
            <>
              <StatsContainer>
                <StatCard>
                  <FaUsers size={24} />
                  <StatValue>{gameData.totalPlayers}</StatValue>
                  <StatLabel>Total Players</StatLabel>
                </StatCard>

                <StatCard>
                  <FaCoins size={24} />
                  <StatValue>{gameData.totalPrizes} USDT</StatValue>
                  <StatLabel>Total Prizes</StatLabel>
                </StatCard>

                <StatCard>
                  <FaTrophy size={24} />
                  <StatValue>{Number(gameData.gameId) - 1}</StatValue>
                  <StatLabel>Games Played</StatLabel>
                </StatCard>
              </StatsContainer>

              <StakeInputContainer>
                <InputWithIcon>
                  <FaWallet />
                  <WalletInput
                    type="text"
                    placeholder="Enter your wallet address (0x...)"
                    value={walletAddress}
                    onChange={(e) => setWalletAddress(e.target.value)}
                    disabled={isLoading}
                  />
                </InputWithIcon>
              </StakeInputContainer>

              <StakeInputContainer>
                <StakeInput
                  type="number"
                  min="1"
                  value={stakeAmount}
                  onChange={handleStakeChange}
                  disabled={isLoading}
                />
                <CurrencyLabel>TICKET</CurrencyLabel>
              </StakeInputContainer>

              <PlayButton
                onClick={handlePlayClick}
                disabled={isLoading || !walletAddress || !gameData.isActive}
                title={!gameData.isActive ?
                  (gameData.status === 'upcoming' ? 'Game starts soon!' : 'No active game at the moment') :
                  'Join the game!'}
              >
                {isLoading ? 'Processing...' : (
                  <>
                    Stake & Play <FaArrowRight style={{ marginLeft: '8px' }} />
                  </>
                )}
              </PlayButton>
            </>
          ) : (
            <p>No active game at the moment. Please try again later.</p>
          )
          }

          {error && <ErrorMessage>{error}</ErrorMessage>}
        </TVContent>
      </TVScreen>
    </div>
  );
};

// Countdown renderer
const renderCountdown = ({ hours, minutes, seconds, completed }) => {
  if (completed) {
    return <span>Drawing in progress...</span>;
  }

  return (
    <span>
      {String(hours).padStart(2, '0')}:
      {String(minutes).padStart(2, '0')}:
      {String(seconds).padStart(2, '0')}
    </span>
  );
};

export default HomePage;
